import React, { useState } from "react";
import "./AddEntity.css";
import ApiManager from "../../ApiManager/ApiManager";
import MapInput from "../MapInput";
import CustomSelect from "../CustomSelect";
import CustomFileUpload from "../CustomFileUpload/CustomFileUpload";
import { useComponentLoading } from "../../utils/useComponentLoading";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";

function AddEntity({
  toggleFunction,
  title,
  formFields,
  apiFunction,
  onSuccess,
  setFileResponse,
  fileResponse,
}) {
  const [formData, setFormData] = useState({});
  const [showLoading, setShowLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [selectedFiles, setSelectedFiles] = useState({});
  const { withLoading, isLoading, getMessage, getProgress } = useComponentLoading();

  const deleteStoredFile = async (fileName) => {
    try {
      const response = await ApiManager.storageDeleteFile(fileName);

      if (!response.ok) {
        throw new Error("Failed to delete file");
      }

      console.log(`File ${fileName} deleted successfully`);
    } catch (error) {
      console.error(`Error deleting file ${fileName}:`, error);
    }
  };

  const handleChange = async (e) => {
    const { name, value, type, checked, multiple, selectedOptions, files } = e.target;

    if (type === "file") {
      // Check if this is a remove action
      if (e.target.action === 'remove' && e.target.storedFileName) {
        try {
          // Delete the file if it exists
          if (fileResponse && fileResponse[name]) {
            await deleteStoredFile(e.target.storedFileName);
          }

          // Clear the file response
          if (setFileResponse) {
            setFileResponse((prev) => ({
              ...prev,
              [name]: null,
            }));
          }

          // Clear the selected file
          setSelectedFiles(prev => ({
            ...prev,
            [name]: ''
          }));
        } catch (error) {
          console.error("Error removing file:", error);
        }
        return;
      }

      // Handle file upload
      try {
        const file = files[0];
        if (file) {
          // Store the file name immediately for display
          setSelectedFiles(prev => ({
            ...prev,
            [name]: file.name
          }));

          setUploadingFiles(prev => ({ ...prev, [name]: true }));

          await withLoading(`uploadFile_${name}`, async (onProgress) => {
            onProgress(10);

            // Delete existing file if there is one
            if (fileResponse && fileResponse[name]) {
              await deleteStoredFile(fileResponse[name]);
              onProgress(20);
            }

            // Get a presigned URL for upload
            const presignedResponse = await ApiManager.storageGetPresignedUploadUrl({
              filename: file.name,
              directory: "uploads"
            });
            onProgress(30);

            if (!presignedResponse.ok) {
              throw new Error("Failed to get presigned URL");
            }

            const presignedData = await presignedResponse.json();
            onProgress(40);

            // The response contains: url, fileName, key, expires
            console.log("Presigned URL data:", presignedData);

            // Upload the file directly to the storage provider
            const uploadResponse = await fetch(presignedData.url, {
              method: 'PUT',
              body: file,
              headers: {
                'Content-Type': file.type
              }
            });
            onProgress(70);

            if (!uploadResponse.ok) {
              throw new Error("Failed to upload file");
            }

            // Save metadata after upload using the fileName from the response
            const metadataResponse = await ApiManager.storageSaveMetadata(presignedData.fileName, presignedData.key);
            onProgress(80);

            if (!metadataResponse.ok) {
              throw new Error("Failed to save metadata");
            }

            onProgress(90);
            console.log("Upload successful. File will expire in:", presignedData.expires);
            if (setFileResponse) {
              setFileResponse((prev) => ({
                ...prev,
                [name]: presignedData.fileName,
              }));
            }
            onProgress(100);
            console.log("upload result", presignedData);
          }, `Uploading ${name}...`, 30000);

          setUploadingFiles(prev => ({ ...prev, [name]: false }));
        }
      } catch (error) {
        console.error("Error uploading file:", error);
        setUploadingFiles(prev => ({ ...prev, [name]: false }));
        // Clear the selected file on error
        setSelectedFiles(prev => ({
          ...prev,
          [name]: ''
        }));
      }
    }

    if (type === "checkbox") {
      if (e.target.dataset.multiselect) {
        // Handle multiselect checkboxes
        setFormData((prev) => {
          const values = prev[name] || [];
          return {
            ...prev,
            [name]: checked
              ? [...values, value]
              : values.filter((v) => v !== value),
          };
        });
      } else {
        // Handle normal checkbox
        setFormData({
          ...formData,
          [name]: checked,
        });
      }
    } else if (multiple) {
      const selectedValues = Array.from(selectedOptions).map(
        (option) => option.value
      );
      setFormData({
        ...formData,
        [name]: selectedValues,
      });
    } else if (type === "datetime-local") {
      const unixTimestamp = Math.floor(new Date(value).getTime() / 1000);
      setFormData({
        ...formData,
        [name]: unixTimestamp,
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === "number" ? Number(value) : value,
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowLoading(true);

    try {
      await withLoading('addEntity', async (onProgress) => {
        onProgress(10);
        const response = await apiFunction(formData);
        onProgress(50);
        console.log("Response:", response);
        if (!response.ok) throw new Error(await response.text());
        onProgress(100);
        toggleFunction();
        if (onSuccess) onSuccess();
      }, 'Adding entity...', 30000);
    } catch (error) {
      console.error("Error submitting form:", error);
      alert(error.message || "Failed to add Entity");
    } finally {
      setTimeout(() => {
        setShowLoading(false);
      }, 500);
    }
  };

  const handleMapInputChange = (fieldName, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  const handleSelectChange = (name, selectedOption) => {
    setFormData(prev => ({
      ...prev,
      [name]: selectedOption?.value || ''
    }));
  };

  // Get loading state for the form
  const isDataLoading = isLoading('addEntity');
  const loadingMessage = getMessage('addEntity');
  const loadingProgress = getProgress('addEntity');

  return (
    <div className="add-form">
      {showLoading ? (
        <div className="loading-container">
          <LoadingSpinner
            size="large"
            message={loadingMessage || "Adding entity..."}
            progress={loadingProgress}
            showProgress={true}
          />
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          {formFields.map((field, index) => (
            <div className={`form-group ${field.type === "mapinput" ? "map-field" : ""}`} key={index}>
              <label>{field.label}:</label>
              {field.type === "mapinput" ? (
                <MapInput
                  label={field.label}
                  onAddressSelect={(locationData) =>
                    handleMapInputChange(field.name, locationData)
                  }
                />
              ) : field.type === "picklist" ? (
                <CustomSelect
                  name={field.name}
                  options={field.options.map(option => ({
                    value: option,
                    label: option
                  }))}
                  value={formData[field.name]}
                  onChange={(option) => handleSelectChange(field.name, option)}
                  placeholder={`Select ${field.label}`}
                />
              ) : field.type === "multiselect" ? (
                <div className="checkbox-group">
                  {field.options.map((option, i) => (
                    <div key={i} className="checkbox-item">
                      <input
                        type="checkbox"
                        name={field.name}
                        value={option}
                        data-multiselect="true"
                        checked={formData[field.name]?.includes(option) || false}
                        onChange={handleChange}
                      />
                      <label>{option}</label>
                    </div>
                  ))}
                </div>
              ) : field.type === "checkbox" ? (
                <div className={`form-group checkbox-group`}>
                  <input
                    type="checkbox"
                    name={field.name}
                    checked={!!formData[field.name]}
                    onChange={handleChange}
                  />
                  <label>{field.label}</label>
                </div>
              ) : field.type === "file" ? (
                <CustomFileUpload
                  name={field.name}
                  onChange={handleChange}
                  required={field.required}
                  accept={field.accept}
                  isUploading={uploadingFiles[field.name]}
                  uploadProgress={uploadingFiles[field.name] ? 50 : 0}
                  value={selectedFiles[field.name]}
                  storedFileName={fileResponse?.[field.name] || ''}
                />
              ) : (
                <input
                  type={field.type}
                  step={field?.step || ""}
                  name={field.name}
                  required={field.required}
                  onChange={handleChange}
                />
              )}
            </div>
          ))}
          <button type="submit">Submit</button>
        </form>
      )}
    </div>
  );
}

export default AddEntity;
